/**
 * Detection / Decisions - Shadow AI boundary controls (Bank I-162).
 *
 * Live data sources:
 *   GET /v1/detection/shadow-ai/boundary              rules, blocklist version, proxies that fetch it
 *   PUT /v1/detection/shadow-ai/boundary              block every unapproved service (or not)
 *   PUT /v1/detection/shadow-ai/services/:key         one service: alert, block, or the default
 *
 * What is enforced where is said in the API's own words (`enforcement`):
 * the customer's proxy blocks; FOCTTA is not in the request path; FOCTTA
 * raises a finding when the proxy log shows a blocked service was not
 * refused.
 */

import { useEffect, useState } from 'react';
import { Loader2, ShieldBan } from 'lucide-react';

import { GlassCard, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getAiBoundary, setAiBoundary, setAiServiceAction, type AiBoundary } from '@/lib/api';

const ACTION_VARIANT = { allow: 'success', alert: 'warning', block: 'critical' } as const;

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { hour12: false });
}

export function ShadowAiBoundaryCard({ canApprove }: { canApprove: boolean }) {
  const [boundary, setBoundary] = useState<AiBoundary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () =>
    getAiBoundary()
      .then((r) => setBoundary(r.boundary))
      .catch((err) => setError(err instanceof Error ? err.message : 'load failed'));

  useEffect(() => {
    void load();
  }, []);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'change failed');
    } finally {
      setBusy(null);
    }
  }

  if (error && !boundary) {
    return (
      <GlassCard className="p-4" data-testid="ai-boundary-card">
        <div className="text-xs text-sev-critical" role="alert">
          {error}
        </div>
      </GlassCard>
    );
  }
  if (!boundary) {
    return (
      <GlassCard className="p-6 text-center text-xs text-muted-foreground" data-testid="ai-boundary-card">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading
      </GlassCard>
    );
  }

  const unapproved = boundary.services.filter((s) => !s.approved);
  const blocked = boundary.services.filter((s) => s.action === 'block');
  const nested = boundary.blocklist.nested ?? [];
  const overridden = boundary.blocklist.overridden ?? [];
  return (
    <GlassCard className="p-0 overflow-hidden" data-testid="ai-boundary-card">
      <div className="px-4 py-2 border-b border-border/40 space-y-1">
        <div className="flex items-center gap-2">
          <ShieldBan className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm">Boundary controls</CardTitle>
        </div>
        <CardDescription className="text-xs" data-testid="ai-boundary-enforcement">
          {boundary.enforcement}
        </CardDescription>
      </div>
      {error && (
        <div className="glass-lite m-2 rounded-md p-2 text-xs text-sev-critical" role="alert">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 px-4 py-2 border-b border-border/30 text-xs">
        <span className="flex-1">
          {boundary.blockUnapproved
            ? 'Every unapproved AI service is blocked unless it is set to alert below.'
            : 'Unapproved AI services raise an alert. Only services set to block below are blocked.'}
        </span>
        {canApprove && (
          <Button
            size="sm"
            variant="glass"
            disabled={busy !== null}
            data-testid="ai-boundary-default"
            onClick={() => void run('default', () => setAiBoundary(!boundary.blockUnapproved))}
          >
            {busy === 'default' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {boundary.blockUnapproved ? 'Alert on unapproved services instead' : 'Block every unapproved service'}
          </Button>
        )}
      </div>

      <ul>
        {unapproved.map((s) => (
          <li key={s.key} className="flex items-center gap-3 px-4 py-2 border-b border-border/30 text-xs" data-testid="ai-boundary-row">
            <span className="w-48 font-medium">{s.name}</span>
            <span className="flex-1 font-mono text-[11px] text-muted-foreground truncate">{s.domains.join(', ')}</span>
            <Badge variant={ACTION_VARIANT[s.action]} className="text-[9px]">
              {s.action}
              {s.unapprovedAction === null ? ' (default)' : ''}
            </Badge>
            {canApprove &&
              (['alert', 'block'] as const)
                .filter((a) => a !== s.unapprovedAction)
                .map((a) => (
                  <Button key={a} size="sm" variant="glass" disabled={busy !== null} onClick={() => void run(s.key, () => setAiServiceAction(s.key, a))}>
                    {a === 'block' ? 'Block' : 'Alert'}
                  </Button>
                ))}
          </li>
        ))}
      </ul>

      <div className="px-4 py-3 space-y-2 text-xs" data-testid="ai-boundary-proxies">
        <div>
          Blocklist version <span className="font-mono">{boundary.blocklist.version}</span>: {boundary.blocklist.domainCount} domain
          {boundary.blocklist.domainCount === 1 ? '' : 's'} from {blocked.length} service{blocked.length === 1 ? '' : 's'}. The proxy fetches it as a Squid dstdomain file (format=squid), a plain domain list for a gateway's custom
          category (format=plain) or JSON.
        </div>
        {boundary.blocklist.exceptions.length > 0 && (
          <div className="text-sev-high" data-testid="ai-boundary-exceptions">
            {boundary.blocklist.exceptions.length} allowed domain{boundary.blocklist.exceptions.length === 1 ? ' sits' : 's sit'} under a blocked one (
            {boundary.blocklist.exceptions.map((e) => e.domain).join(', ')}). The proxy must exempt the exceptions list (list=exceptions) from the block, never allow it:
            on Squid `http_access deny foctta_ai_blocked !foctta_ai_exceptions`. An allow line placed before your own rules would let
            anyone reach these domains past them.
          </div>
        )}
        {nested.length > 0 && (
          <div className="text-sev-high" data-testid="ai-boundary-nested">
            {nested.length} blocked domain{nested.length === 1 ? ' sits' : 's sit'} under an allowed one (
            {nested.map((n) => n.domain).join(', ')}). Load the nested list (list=nested) as a deny rule before the exceptions, or the proxy lets
            them through.
          </div>
        )}
        {overridden.length > 0 && (
          <div className="text-sev-high" data-testid="ai-boundary-overridden">
            {overridden.length} allowed domain{overridden.length === 1 ? ' is' : 's are'} refused by the proxy anyway (
            {overridden.map((o) => o.domain).join(', ')}): each sits under a blocked domain that is itself under an allowed one, and at that depth
            the block wins.
          </div>
        )}
        {boundary.proxies.length === 0 ? (
          <div className={blocked.length > 0 ? 'text-sev-critical' : 'text-muted-foreground'} data-testid="ai-boundary-no-proxy">
            No proxy has fetched the blocklist{blocked.length > 0 ? ', so nothing set to block is being blocked yet' : ''}. Issue a web_proxy service credential
            (Admin, service credentials) and have the proxy fetch GET /v1/detection/shadow-ai-blocklist every few minutes, keeping its last list when a fetch fails.
          </div>
        ) : (
          <ul className="space-y-1">
            {boundary.proxies.map((p) => (
              <li key={p.credentialId} data-testid="ai-boundary-proxy">
                <span className="font-medium">{p.instanceLabel}</span> last fetched {when(p.lastFetchedAt)}
                {p.stale && <Badge variant="critical" className="ml-2 text-[9px]">no fetch for 24 hours</Badge>}
                {p.behind && <Badge variant="warning" className="ml-2 text-[9px]">holds an older list</Badge>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </GlassCard>
  );
}
